import { ChartContainer } from '@components/chart';
import { DownloadSVG, UserMic2 } from '@components/icon/svg';
import CustomToggle from '@components/shared/CustomToggle';
import { BarChart, PieChart } from '@mui/icons-material';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import DesktopMacOutlinedIcon from '@mui/icons-material/DesktopMacOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import { alpha, Box, Card, Grid, Tooltip, Typography, useTheme } from '@mui/material';
import { handleSaveImage } from '@utils/saveChartImage';
import ReactECharts from 'echarts-for-react';
import _ from 'lodash';
import { useRef, useState } from 'react';

const colorList = [
  '#C9B7FF', // Lavender
  '#9BC5FF', // Baby Blue
  '#66C0FF', // Sky Blue
  '#6EE7B7', // Mint Green
  '#F9D17B', // Pastel Yellow
  '#FCA5A5', // Soft Coral
  '#FDB4FF', // Light Pink
  '#FFB366', // Peach Orange
  '#7DD3FC', // Light Cyan Blue
  '#A78BFA', // Medium Purple
  '#FFD6A5', // Apricot
  '#B5E48C', // Lime Green
  '#FFADAD', // Warm Pink
  '#9BF6FF', // Aqua
  '#BDB2FF', // Periwinkle
  '#C8FFD4', // Mint Cream
  '#FFCBC1', // Peach Blush
  '#FFF2B2', // Soft Lemon
  '#D7C0F9', // Pale Violet
  '#A0E7E5', // Turquoise Pastel
];

/**
 * Compact stat card for Response Insights
 */
function CompactStatCard({ icon: Icon, label, value, color, tooltip }) {
  const theme = useTheme();
  return (
    <Card
      variant="outlined"
      sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
        boxShadow: `0 1px 3px ${alpha(theme.palette.grey[500], 0.08)}`,
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: `0 2px 8px ${alpha(theme.palette.grey[500], 0.12)}`,
        },
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 1.5,
          backgroundColor: alpha(color || theme.palette.primary.main, 0.1),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 22, color: color || theme.palette.primary.main }} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: '0.75rem', fontWeight: 500 }}
          >
            {label}
          </Typography>
          {tooltip && (
            <Tooltip title={tooltip}>
              <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'pointer' }} />
            </Tooltip>
          )}
        </Box>
        <Typography variant="h5" fontWeight={600} sx={{ lineHeight: 1.2 }}>
          {value ?? 0}
        </Typography>
      </Box>
    </Card>
  );
}

export default function ResponseInsight({
  completionStatistic,
  deviceUsages,
  surveyParticipant,
  timeToComplete,
  voiceFeatureUsages,
  isPrimary,
  hideVoiceFeatureUsages,
  canEdit,
}) {
  const theme = useTheme();
  return (
    <Box p={{ xs: 2, md: 2, lg: 4 }}>
      {/* Compact Stats Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={3} lg={2}>
          <CompactStatCard
            icon={PeopleAltOutlinedIcon}
            label="Human Participants"
            value={(completionStatistic?.total_completed_human || 0) + (completionStatistic?.total_incomplete || 0)}
            color={theme.palette.primary.main}
            tooltip={`Completed: ${completionStatistic?.total_completed_human || 0}, Incomplete: ${completionStatistic?.total_incomplete || 0}`}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={2}>
          <CompactStatCard
            icon={SmartToyOutlinedIcon}
            label="AI Participants"
            value={completionStatistic?.total_completed_ai}
            color={theme.palette.info.main}
          />
        </Grid>
      </Grid>

      {/* Charts Grid - More Compact */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6} lg={4}>
          <SurveyTimeToComplete
            timeToComplete={timeToComplete}
            canAddToReport={canEdit}
            hasData={timeToComplete?.length > 0}
            isPrimary={isPrimary}
            controller={<DownloadSVG />}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <SurveyGeographyMap
            canAddToReport={canEdit}
            surveyMap={surveyParticipant ?? []}
            hasData={surveyParticipant?.length > 0}
            isPrimary={isPrimary}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <SurveyDeviceUsages
            canAddToReport={canEdit}
            deviceUsages={deviceUsages || []}
            hasData={deviceUsages?.length > 0}
            isPrimary={isPrimary}
          />
        </Grid>
        {!hideVoiceFeatureUsages && (
          <Grid item xs={12} md={6} lg={4}>
            <SurveyVoiceOptionUsage
              voiceFeatureUsages={voiceFeatureUsages || []}
              isPrimary={isPrimary}
              hasData={voiceFeatureUsages?.length > 0}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

function binData(data) {
  const bins = {
    '0-15': 0,
    '15-30': 0,
    '30-45': 0,
    '45-60': 0,
    '60+': 0,
  };

  data.forEach((item) => {
    const time = parseFloat(item);

    if (time >= 0 && time < 15) {
      bins['0-15'] += 1;
    } else if (time >= 15 && time < 30) {
      bins['15-30'] += 1;
    } else if (time >= 30 && time < 45) {
      bins['30-45'] += 1;
    } else if (time >= 45 && time < 60) {
      bins['45-60'] += 1;
    } else {
      bins['60+'] += 1;
    }
  });

  return Object.values(bins);
}

const SurveyTimeToComplete = ({ timeToComplete, hasData, isPrimary, canAddToReport }) => {
  const [chartType, setChartType] = useState('bar');
  const chartRef = useRef(null);
  const histogramData = binData(timeToComplete);

  const option = {
    grid: { top: 8, right: 8, bottom: 54, left: 55 },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
    },
    xAxis: {
      type: 'category',
      data: ['0-15', '15-30', '30-45', '45-60', '60+'], // Categories from the data
      name: 'Time Range (minutes)', // Axis label
      nameLocation: 'middle',
      nameGap: 30,
    },
    yAxis: {
      type: 'value',
      name: 'Number of Respondents', // Axis label
      nameLocation: 'middle',
      nameGap: 40,
    },
    series: [
      {
        name: 'Count',
        type: 'bar',
        data: histogramData, // Values corresponding to each category
        itemStyle: {
          color: '#C9B7FF',
        },
      },
    ],
  };
  const pieOption = {
    grid: { top: 8, right: 8, bottom: 54, left: 55 },
    tooltip: {
      trigger: 'item',
    },
    legend: {
      orient: 'vertical',
      left: 'left',
    },
    series: [
      {
        name: 'Count',
        type: 'pie',
        radius: '80%',
        itemStyle: {
          normal: {
            color: (params) => {
              const index = params.dataIndex;
              return colorList[index % colorList.length];
            },

            textStyle: {
              fontSize: 14,
              color: (params) => {
                const index = params.dataIndex;
                return colorList[index % colorList.length];
              },
            },
          },
        },
        data: histogramData.map((count, index) => {
          const name = index === 4 ? '60+' : `${index * 15}-${(index + 1) * 15}`;
          return { name, value: count, label: { show: count > 0 } };
        }),
      },
    ],
  };

  return (
    <ChartContainer
      compact
      canAddToReport={canAddToReport}
      hideToolbar={!hasData}
      title={'Completion Time (min)'}
      chartRef={chartRef}
      icon={<AccessTimeOutlinedIcon sx={{ fontSize: 18 }} />}
      handleSaveImage={() => handleSaveImage({ chartRef, fileName: 'Study Completion Time' })}
      controller={
        <>
          <CustomToggle
            options={[
              { value: 'bar', icon: BarChart, label: 'Bar Chart view' },
              { value: 'pie', icon: PieChart, label: 'Pie chart view' },
            ]}
            value={chartType}
            onChange={(value) => setChartType(value)}
          />
        </>
      }
    >
      {hasData ? (
        <ReactECharts
          ref={chartRef}
          option={chartType === 'bar' ? option : pieOption}
          style={{ height: '100%', width: '100%' }}
          notMerge={true} // Ensures old options are completely replaced
          lazyUpdate={false}
        />
      ) : (
        <NoDataAvailable
          message={
            isPrimary ? 'Data not available' : 'This insights is not available for uploaded data'
          }
        />
      )}
    </ChartContainer>
  );
};

const SurveyGeographyMap = ({ surveyMap, hasData, isPrimary, canAddToReport }) => {
  const [chart, setChart] = useState('pie');
  const chartRef = useRef();
  const barOptions = {
    grid: { top: 8, right: 8, bottom: 24, left: 55 },
    xAxis: {
      type: 'category',
      axisLabel: {
        inside: true,
        color: '#000',
        rotate: 90,
      },
      z: 10,
      data: _.map(surveyMap, 'country'),
    },
    yAxis: {
      type: 'value',
      name: 'Number of Respondents',
      nameLocation: 'middle',
      nameGap: 40,
    },
    dataZoom: [
      {
        type: 'inside',
        startValue: 0,
        endValue: 100,
      },
    ],
    series: [
      {
        data: _.map(surveyMap, 'participation_count'),
        type: 'bar',
        itemStyle: {
          color: '#C9B7FF',
        },
        smooth: true,
      },
    ],
    tooltip: {
      trigger: 'axis',
    },
  };

  const pieOptions = {
    tooltip: {
      trigger: 'item',
    },

    series: [
      {
        name: 'Country',
        type: 'pie',
        radius: '80%',
        itemStyle: {
          normal: {
            color: (params) => {
              const index = params.dataIndex;
              return colorList[index % colorList.length];
            },

            textStyle: {
              fontSize: 14,
              color: (params) => {
                const index = params.dataIndex;
                return colorList[index % colorList.length];
              },
            },
          },
        },
        data: surveyMap.map((item) => ({
          name: item.country,
          value: item.participation_count,
        })),
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
      },
    ],
  };

  return (
    <ChartContainer
      compact
      canAddToReport={canAddToReport}
      hideToolbar={!hasData}
      title={'Demographics by Country'}
      icon={<OutlinedFlagIcon sx={{ fontSize: 18 }} />}
      chartRef={chartRef}
      handleSaveImage={() =>
        handleSaveImage({ chartRef, fileName: 'Response Demographics by Country' })
      }
      controller={
        <>
          <CustomToggle
            options={[
              { value: 'bar', icon: BarChart, label: 'Bar Chart view' },
              { value: 'pie', icon: PieChart, label: 'Pie chart view' },
            ]}
            value={chart}
            onChange={(value) => setChart(value)}
          />
        </>
      }
    >
      {hasData ? (
        <ReactECharts
          ref={chartRef}
          option={chart === 'bar' ? barOptions : pieOptions}
          style={{ height: '100%' }}
          notMerge={true} // Ensures old options are completely replaced
          lazyUpdate={false}
        />
      ) : (
        <NoDataAvailable
          message={
            isPrimary ? 'Data not available' : 'This insights is not available for uploaded data'
          }
        />
      )}
    </ChartContainer>
  );
};

const SurveyDeviceUsages = ({ deviceUsages, hasData, isPrimary, canAddToReport }) => {
  const [chart, setChart] = useState('bar');

  const chartRef = useRef();
  const sortedDeviceUsages = _.sortBy(deviceUsages, 'usage_count');
  const options = {
    grid: { top: 8, right: 30, bottom: 24, left: 100 },
    color: '#7d5ae8',
    xAxis: {
      type: 'value',
    },
    yAxis: {
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        color: '#999',
      },
      type: 'category',
      data: _.map(sortedDeviceUsages, 'device_name'),
    },

    series: [
      {
        data: _.map(sortedDeviceUsages, 'usage_count'),
        type: 'bar',
        itemStyle: {
          color: '#C9B7FF',
        },
        label: {
          show: true,
          position: 'right',
        },
        smooth: true,
      },
    ],
    tooltip: {
      trigger: 'axis',
    },
  };

  const pieOptions = {
    grid: { top: 8, right: 30, bottom: 24, left: 100 },
    color: ['#7d5ae8'],
    tooltip: {
      trigger: 'item',
    },
    series: [
      {
        data: _.map(sortedDeviceUsages, (item) => ({
          value: item.usage_count,
          name: item.device_name,
        })),
        itemStyle: {
          normal: {
            color: (params) => {
              const index = params.dataIndex;
              return colorList[index % colorList.length];
            },

            textStyle: {
              fontSize: 14,
              color: (params) => {
                const index = params.dataIndex;
                return colorList[index % colorList.length];
              },
            },
          },
        },
        type: 'pie',
        radius: '80%',
        label: {
          show: true,
          position: 'outside',
          formatter: '{b}: {c} ({d}%)',
        },
        smooth: true,
      },
    ],
  };

  return (
    <ChartContainer
      compact
      canAddToReport={canAddToReport}
      hideToolbar={!hasData}
      title={'Device Usage'}
      icon={<DesktopMacOutlinedIcon sx={{ fontSize: 18 }} />}
      chartRef={chartRef}
      handleSaveImage={() => handleSaveImage({ chartRef, fileName: 'Device Usages' })}
      controller={
        <>
          <CustomToggle
            options={[
              { value: 'bar', icon: BarChart, label: 'Bar Chart view' },
              { value: 'pie', icon: PieChart, label: 'Pie chart view' },
            ]}
            value={chart}
            onChange={(value) => setChart(value)}
          />
        </>
      }
    >
      {hasData ? (
        <ReactECharts
          ref={chartRef}
          option={chart === 'bar' ? options : pieOptions}
          style={{ height: '100%' }}
          notMerge={true}
          lazyUpdate={false}
        />
      ) : (
        <NoDataAvailable
          message={
            isPrimary ? 'Data not available' : 'This insights is not available for uploaded data'
          }
        />
      )}
    </ChartContainer>
  );
};

const SurveyVoiceOptionUsage = ({ voiceFeatureUsages, hasData, isPrimary, canAddToReport }) => {
  const chartRef = useRef();
  const sortData = _.sortBy(voiceFeatureUsages, 'usage_frequency');
  const data = _.map(sortData, 'count');
  const category = _.map(sortData, 'usage_frequency');
  const options = {
    grid: { top: 8, right: 8, bottom: 54, left: 55 },
    xAxis: {
      type: 'category',
      name: 'Usage frequency',
      nameLocation: 'middle',
      nameGap: 40,
      data: category,
    },
    yAxis: {
      type: 'value',
      name: 'Number of Respondents',
      nameLocation: 'middle',
      nameGap: 40,
    },
    series: [
      {
        name: 'Users',
        data: data || [],
        type: 'bar',
        itemStyle: {
          color: '#C9B7FF',
        },
      },
    ],
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
    },
  };
  return (
    <ChartContainer
      compact
      canAddToReport={canAddToReport}
      hideToolbar={!hasData}
      title={'Voice Feature Usage'}
      icon={<UserMic2 style={{ width: 18, height: 18 }} />}
      chartRef={chartRef}
      handleSaveImage={() => handleSaveImage({ chartRef, fileName: 'Voice Feature Usages' })}
    >
      {hasData ? (
        <ReactECharts ref={chartRef} option={options} style={{ height: '100%' }} />
      ) : (
        <NoDataAvailable
          message={
            isPrimary ? 'Data not available' : 'This insights is not available for uploaded data'
          }
        />
      )}
    </ChartContainer>
  );
};

const NoDataAvailable = ({ message }) => {
  return (
    <Box
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
      }}
    >
      <Typography variant="subtitle2">{message}</Typography>
    </Box>
  );
};
